import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import cn from "classnames";
import { useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Icon } from "semantic-ui-react";

import { type RootState } from "../../../../Application/globaltypes/redux";
import dataservice from "../../services/surveysDataService";
import {
  resetSelectedAnswer,
  selectSurveySelectedAnswerData,
  selectSurveySelectedAnswerValue,
  selectSurveySelectedQuestionData,
  selectSurveySelectedQuestionValue,
} from "../../state/slices/surveyPerformanceSlice";
import { fetchSelectedAnswerData, fetchSelectedQuestionData } from "../../state/thunks/surveyPerformanceThunk";
import { type SurveyQuestion, type SurveyQuestionAnswer, SurveyQuestionType } from "../../types/state";

import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import CardReporting, { type CardReportingItem } from "../../../../../components/cards/CardReporting/CardReporting";
import { HorizontalBarWrapper, TickValueType } from "../../../../../components/charts";
import { RequestStatusRenderer } from "../../../../../components/requestStatsRenderer/RequestStatusRenderer";
import SurveyPerformanceDrilldownList from "../DrilldownList/SurveyPerformanceDrilldownList";

import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import {
  fallbackToZero,
  hasSingleAccountFilter,
  horizontalBarChartMargins,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import "./SurveyPerformanceQuestionDrillDown.scss";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";

export interface Props extends PropsFromRedux {
  surveyId: number;
  flowId?: number;
  question: SurveyQuestion;
  answer: SurveyQuestionAnswer | undefined;
  hideHeader?: boolean;
  filter: PerformanceWithAccountFilter;
  onSelectAnswer: (answer: SurveyQuestionAnswer | undefined) => void;
  onNextQuestion: () => void;
  onPrevQuestion: () => void;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export interface FormattedAnswers {
  id: string;
  category: string;
  value: number;
  fillColor?: string;
  iconClass?: string;
}

const RatingScaleCount = 10;

export const SurveyPerformanceQuestionDrillDown: React.FC<Props> = ({
  fetchQuestionData,
  fetchAnswerData,
  accountId,
  surveyId,
  flowId,
  question,
  answer,
  filter,
  hideHeader,

  onSelectAnswer,
  onNextQuestion,
  onPrevQuestion,

  selectedQuestionStatus,
  selectedQuestion,
  selectedAnswerStatus,
  selectedAnswer,

  resetAnswerTable,
  setHasExportData,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const [formattedData, setFormattedData] = useState<FormattedAnswers[]>([]);
  const reportEnabled = useFeatureFlag(FeatureFlags.SurveyReport);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: selectedAnswerStatus.status === RStatus.Got && !!selectedAnswer?.Users.length,
      tooltipContent: "No data available for export. A question response must be selected.",
    });
  }, [setHasExportData, selectedAnswerStatus, selectedAnswer]);

  useEffect(() => {
    if (reportEnabled) {
      fetchQuestionData(surveyId, question.QuestionId, { ...filter, flowId });
    }
  }, [fetchQuestionData, filter, flowId, reportEnabled, question.QuestionId, surveyId, accountId]);

  useEffect(() => {
    if (!reportEnabled) {
      return;
    }
    // Only try to fetch the table if the answer has people to get information on
    if (answer && answer.AnsweredPeople !== 0) {
      fetchAnswerData(surveyId, question.QuestionId, answer.AnswerId, { ...filter, flowId });
    } else {
      dataservice.cancelSurveyAnswerDetails(); // Prevents table from showing old answer request
      resetAnswerTable();
    }
  }, [
    fetchAnswerData,
    answer,
    question.QuestionId,
    surveyId,
    resetAnswerTable,
    filter,
    flowId,
    reportEnabled,
    accountId,
  ]);

  useEffect(() => {
    const data = selectedQuestion?.Answers?.map((a) => {
      return {
        id: a.AnswerId,
        category: a.AnswerId === "OTHER" ? "Other" : a.AnswerText,
        value: a.AnsweredPeople,
        fillColor: reportingColors.greyLight,
      };
    });

    setFormattedData(data);
  }, [selectedQuestion]);

  const selectAnswer = (index: number | null): void => {
    if (index !== null && index !== -1) {
      onSelectAnswer(selectedQuestion.Answers[index]);
    }
  };

  // For the Free Text question type, there is only one answer, and we hide the usual answer picker from the UI.
  useEffect(() => {
    if (selectedQuestion.QuestionType === SurveyQuestionType.FreeText) onSelectAnswer(selectedQuestion.Answers[0]);
  }, [onSelectAnswer, selectedQuestion.QuestionType, selectedQuestion.Answers]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div data-testid="survey-drill-down" className="surveyDrillDownRoot">
      {!hideHeader && (
        <div className="drill-down-navigation ">
          <div className="questionTitle">{question.QuestionText}</div>
          <div className="assessmentActions">
            <button
              title="Previous Question"
              type="button"
              data-testid="previous-question"
              className={cn("invisibleButton", "leftSpacing")}
              onClick={onPrevQuestion}
            >
              <Icon className="chevron left" />
            </button>
            <button
              title="Next Question"
              type="button"
              data-testid="next-question"
              className={cn("invisibleButton", "leftSpacing")}
              onClick={onNextQuestion}
            >
              <Icon className="chevron right" />
            </button>
          </div>
        </div>
      )}

      <div className="summary-fields-container">
        <RequestStatusRenderer state={selectedQuestionStatus}>
          <CardReporting
            items={
              [
                {
                  statDescription: "Completions",
                  stat: fallbackToZero(selectedQuestion.Completions),
                  popupHeader: "Completions",
                  popupBody: "Number of completions",
                },
                {
                  statDescription: "Completion Rate",
                  stat: fallbackToZero(selectedQuestion.CompletionRate) * 100,
                  isStatPercentage: true,
                  popupHeader: "Completion Rate",
                  popupBody: "Percentage of users that have completed the question.",
                  width: "13.75rem",
                },
                {
                  statDescription: "Skipped",
                  stat: selectedQuestion.Skipped,
                  popupHeader: "Skipped",
                  popupBody: "Times question has been skipped",
                  width: "7.5rem",
                },
              ] as CardReportingItem[]
            }
          />
        </RequestStatusRenderer>
      </div>
      {selectedQuestion.QuestionType !== SurveyQuestionType.FreeText && (
        <div
          className={cn("question-details-container", {
            "rating-scale": question.QuestionType === SurveyQuestionType.RatingScale,
          })}
        >
          <RequestStatusRenderer state={selectedQuestionStatus}>
            {!!formattedData?.length && (
              <HorizontalBarWrapper
                title="Responses"
                margins={{ ...horizontalBarChartMargins, left: 202 }}
                data={formattedData}
                selectedValue={answer?.AnswerId}
                onChange={selectAnswer}
                countBarsToShowAtOnce={question.QuestionType === SurveyQuestionType.RatingScale ? RatingScaleCount : 7}
                tooltipFormatter={(d) => roundToTwoDigits(d.value)}
                xTickValueType={TickValueType.IntegersOnly}
              />
            )}
          </RequestStatusRenderer>
        </div>
      )}
      <div
        className={cn("answer-details-container", {
          "free-text": selectedQuestion.QuestionType === SurveyQuestionType.FreeText,
        })}
      >
        <RequestStatusRenderer state={selectedAnswerStatus}>
          {selectedAnswer && (
            <SurveyPerformanceDrilldownList
              answers={selectedAnswer.Users}
              isFreeTextCompatible={selectedQuestion.QuestionType !== SurveyQuestionType.RatingScale}
              hasSingleAccountFilter={hasSingleAccountFilter(filter)}
              isFreeTextQuestion={selectedQuestion.QuestionType === SurveyQuestionType.FreeText}
              includeAccounts={includeAccounts}
              childAccountId={childAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
            />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    selectedQuestionStatus: selectSurveySelectedQuestionData(state),
    selectedQuestion: selectSurveySelectedQuestionValue(state),
    selectedAnswerStatus: selectSurveySelectedAnswerData(state),
    selectedAnswer: selectSurveySelectedAnswerValue(state),
    accountId: state.userProfile.accountId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchQuestionData: bindActionCreators(fetchSelectedQuestionData, dispatch),
    fetchAnswerData: bindActionCreators(fetchSelectedAnswerData, dispatch),
    resetAnswerTable: bindActionCreators(resetSelectedAnswer, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveyPerformanceQuestionDrillDown);
export default ConnectedComponent;
